import type { ColumnsType, AnyObject } from "@similarweb/pro-logic-components";
import { findAndSetColumnSortOrder, ChangeCell } from "@similarweb/pro-logic-components";

import {
    DefaultCell,
    DefaultCellRightAlign,
    SimpleIconCell,
    TrafficShareWithVisits,
    UrlCellWebsiteAnalysis,
    WaKeywordPosition,
} from "app/components/React/Table/cells";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { LinkCellRightAligned } from "app/components/React/Table/cells/LinkCellRightAligned";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { hasDigitalMarketingPermission, hasMarketResearchPermission } from "app/components/SideBar/utils";
import { NO_DATA_SIGN, THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { roundNumberFilter } from "app/filters/ngFilters";
import { allTrackers } from "app/services/track/track";
import { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { AdColumnUnit } from "app/components/keyword-research/AdColumnUnit";
import { DrilldownWrapper } from "app/components/keyword-research/landing-pages/styledComponents";
import { StyledEnrichButton, StyledEnrichButtonNumber } from "../../landing-pages/StyledComponents";
import { formatPercentageWithDynamicPrecision } from "../../website-keywords/table/changeValueFormatters";

import {
    COLUMN_TITLES,
    COLUMN_TOOLTIPS,
    TABLE_COLUMN_AD_DETAILS,
    TABLE_COLUMN_AD_TYPE,
    TABLE_COLUMN_AVG_CPC,
    TABLE_COLUMN_CPC,
    TABLE_COLUMN_AVG_VOLUME,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_CLICKS_SHARE,
    TABLE_COLUMN_KEYWORD,
    TABLE_COLUMN_KEYWORDS_COUNT,
    TABLE_COLUMN_LANDING_PAGE,
    TABLE_COLUMN_POSITION,
    TABLE_COLUMN_TOP_KEYWORD,
    TABLE_COLUMN_VOLUME,
    TABLE_COLUMN_DOMAIN,
} from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";
import { PAGE_SIZE } from "./searchAdsTableSharedConsts";

export const getSearchAdsTableColumns = (
    visibleColumns: string[],
    currentPage: number,
    pageSize: number,
    sortConfig?: { sort: string; asc: boolean },
    toggleExpandRow?: (key: number) => void,
    total?: number,
    isAllowedToSeePaid?: boolean,
    websiteKeywordsState: string = "organicsearch_websites_v2_traffic_keywords",
) =>
    (
        [
            {
                title: "",
                align: "center",
                fixed: "left",
                key: "index",
                ...getIndexCellPropsForTableV2(currentPage, pageSize),
            },
            {
                dataIndex: TABLE_COLUMN_KEYWORD,
                title: `${translate(COLUMN_TITLES[TABLE_COLUMN_KEYWORD])} ${
                    total ? `(${roundNumberFilter()(total)})` : ""
                }`,
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_KEYWORD]),
                width: "40%",
                render: (rowCellData) => (
                    <DefaultCell
                        value={rowCellData}
                        {...rowCellData}
                    />
                ),
            },
            {
                dataIndex: TABLE_COLUMN_AD_TYPE,
                key: TABLE_COLUMN_AD_TYPE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_AD_TYPE]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_AD_TYPE]),
                fixed: "left",
                render: ({ iconDetails, ...rest }) => (
                    <SimpleIconCell
                        row={{ iconDetails }}
                        {...(rest as ITableCellProps)}
                    />
                ),
                width: 72,
            },
            {
                dataIndex: TABLE_COLUMN_AD_DETAILS,
                title: `${translate(COLUMN_TITLES[TABLE_COLUMN_AD_DETAILS])} ${
                    total ? `(${roundNumberFilter()(total)})` : ""
                }`,
                fixed: "left",
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_AD_DETAILS]),
                render: (rowCellData) => (
                    <AdColumnUnit
                        {...rowCellData}
                        fixedImageHeight={false}
                    />
                ),
                width: 430,
            },
            {
                dataIndex: TABLE_COLUMN_CLICKS_SHARE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_CLICKS_SHARE]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_CLICKS_SHARE]),
                render: ({ totalClicks, value }) => (
                    <TrafficShareWithVisits
                        row={{ totalClicks }}
                        value={value}
                        applyAbbrNumberFilter
                        undefinedSign={NO_DATA_SIGN}
                        totalVisitsRowProp="totalClicks"
                        thresholdForTotalFilter={50}
                        thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                        displayLessThanThreshold={true}
                    />
                ),
                width: 150,
                sorter: true,
            },

            {
                dataIndex: TABLE_COLUMN_CLICKS_CHANGE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_CLICKS_CHANGE]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_CLICKS_CHANGE]),
                render: (rowCellData) => {
                    return (
                        <ChangeCell
                            value={rowCellData}
                            formattedValue={formatPercentageWithDynamicPrecision(rowCellData)}
                        />
                    );
                },
                align: "right",
                width: 91,
                sorter: true,
            },
            {
                dataIndex: TABLE_COLUMN_DOMAIN,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_DOMAIN]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_DOMAIN]),
                render: ({ value, favicon }) => {
                    const { isWWW, country, duration, webSource } = proNavigator.getParams();
                    const props = {
                        domain: value,
                        icon: favicon,
                        internalLink: proNavigator.href(websiteKeywordsState, {
                            key: value,
                            country,
                            duration,
                            webSource,
                            isWWW,
                            selectedPageTab: isAllowedToSeePaid
                                ? EKeywordTabs[EKeywordTabs.Paid]
                                : EKeywordTabs[EKeywordTabs.Total],
                        }),
                        trackInternalLink: (e) => {
                            e.stopPropagation();
                            allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/${value}`);
                        },
                        externalLink: `http://${value}`,
                        trackExternalLink: (e) => {
                            e.stopPropagation();
                            allTrackers.dispatchTrackEvent("External Link", "click", `Table/${value}`);
                        },
                        hideTrackButton: true,
                    };
                    return (
                        <CoreWebsiteCell
                            domain={value}
                            textAndIconMargin={"8px"}
                            {...props}
                        />
                    );
                },
                width: 152,
                sortable: false,
            },
            {
                dataIndex: TABLE_COLUMN_VOLUME,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_VOLUME]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_VOLUME]),
                width: 88,
                align: "right",
                render: (rowCellData) => (
                    <DefaultCellRightAlign
                        value={rowCellData}
                        {...rowCellData}
                    />
                ),
                sorter: true,
            },
            {
                dataIndex: TABLE_COLUMN_AVG_CPC,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_AVG_CPC]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_AVG_CPC]),
                width: 96,
                align: "right",
                render: (rowCellData) => (
                    <DefaultCellRightAlign
                        value={rowCellData}
                        {...rowCellData}
                    />
                ),
                sorter: true,
            },
            {
                dataIndex: TABLE_COLUMN_KEYWORDS_COUNT,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_KEYWORDS_COUNT]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_KEYWORDS_COUNT]),
                width: 100,
                onCell: (record) => ({
                    onClick: () => {
                        if (record[TABLE_COLUMN_KEYWORDS_COUNT] > 0) {
                            toggleExpandRow(record.key);
                        }
                    },
                }),
                render: (rowCellData) => {
                    const isDisabled = !rowCellData;

                    const onClick = (e: Event) => {
                        allTrackers.dispatchTrackEvent("Button", "click", "Search Ads Pages/trend drilldown");
                    };

                    return (
                        <DrilldownWrapper>
                            <StyledEnrichButton
                                height={24}
                                onClick={isDisabled ? undefined : onClick}
                                iconName="chev-down"
                                isDisabled={isDisabled}
                            >
                                <StyledEnrichButtonNumber isDisabled={isDisabled}>
                                    {rowCellData}
                                </StyledEnrichButtonNumber>
                            </StyledEnrichButton>
                        </DrilldownWrapper>
                    );
                },
                sorter: true,
            },
            {
                dataIndex: TABLE_COLUMN_TOP_KEYWORD,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_TOP_KEYWORD]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_TOP_KEYWORD]),
                width: 100,

                render: (rowCellData) => {
                    return (
                        <LinkCellRightAligned
                            {...rowCellData}
                            value={rowCellData}
                            displayLongText={true}
                            getLinkCallback={
                                hasDigitalMarketingPermission() && hasMarketResearchPermission()
                                    ? () =>
                                          proNavigator.href("organicsearch_keywordAnalysis_overview_2", {
                                              keyword: rowCellData,
                                              ...proNavigator.getParams(),
                                          })
                                    : undefined
                            }
                            tooltipContentCallback={() =>
                                translate("keyword.research.search.ads.table.tooltip.topKeyword", {
                                    keyword: rowCellData,
                                })
                            }
                            tooltipMaxWidth={400}
                        />
                    );
                },
            },
            {
                dataIndex: TABLE_COLUMN_POSITION,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_POSITION]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_POSITION]),
                width: 91,
                align: "right",
                render: (rowCellData) => {
                    return (
                        <WaKeywordPosition
                            field={TABLE_COLUMN_POSITION}
                            row={rowCellData}
                            {...rowCellData}
                        />
                    );
                },
                sorter: true,
            },
            {
                dataIndex: TABLE_COLUMN_LANDING_PAGE,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_LANDING_PAGE]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_LANDING_PAGE]),
                width: 240,
                render: (rowCellData) => (
                    <UrlCellWebsiteAnalysis
                        value={rowCellData}
                        {...rowCellData}
                    />
                ),
            },

            {
                dataIndex: TABLE_COLUMN_AVG_VOLUME,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_AVG_VOLUME]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_AVG_VOLUME]),
                width: 100,
                align: "right",
                render: (rowCellData) => (
                    <DefaultCellRightAlign
                        value={rowCellData}
                        {...rowCellData}
                    />
                ),
            },
            {
                dataIndex: TABLE_COLUMN_CPC,
                title: translate(COLUMN_TITLES[TABLE_COLUMN_CPC]),
                tooltipSubtitle: translate(COLUMN_TOOLTIPS[TABLE_COLUMN_CPC]),
                width: 100,
                align: "right",
                sorter: true,
                render: (rowCellData) => (
                    <DefaultCellRightAlign
                        value={rowCellData}
                        {...rowCellData}
                    />
                ),
            },
        ] as ColumnsType<AnyObject>
    )
        .filter((col) => visibleColumns.includes(col.dataIndex as string) || !col.dataIndex)
        .map((col) => findAndSetColumnSortOrder(col, sortConfig.sort, sortConfig.asc));

export const getKeywordsCountTableColumnsConfig = (total, currentPage, sortConfig): ColumnsType<AnyObject> =>
    [
        ...getSearchAdsTableColumns(
            [
                TABLE_COLUMN_KEYWORD,
                TABLE_COLUMN_CLICKS_SHARE,
                TABLE_COLUMN_CLICKS_CHANGE,
                TABLE_COLUMN_VOLUME,
                TABLE_COLUMN_AVG_VOLUME,
                TABLE_COLUMN_CPC,
            ],
            currentPage,
            PAGE_SIZE,
            sortConfig,
        ),
    ]
        .filter((col) => col.dataIndex)
        .map((col) => findAndSetColumnSortOrder(col, sortConfig.sort, sortConfig.asc));
